import gsap from "gsap";
import { TeamListingFilter } from "../TeamListingFilter/TeamListingFilter";

class TeamListing {
  constructor(component) {
    this.refs = {
      grid: '.js-team-listing-grid',
      gridsWrap: '.js-team-listing-grids-wrap',
      filter: '.js-team-members-listing-filter',
      listItem: '.js-team-listing-grid-list-item',
      teamTagsData: '#team-tags-data',
      teamListingSidebar: '.js-team-listing-sidebar',
    };

    this.dom = {
      $component: component,
      $grids: component.querySelectorAll(this.refs.grid),
      $gridsWrap: component.querySelector(this.refs.gridsWrap),
      $filterModules: component.querySelectorAll(this.refs.filter),
      $teamTagsData: component.querySelector(this.refs.teamTagsData),
      $teamListingSidebar: component.querySelector(this.refs.teamListingSidebar),
    };

    this.state = {
      filterInstances: {},
      activeFilters: [],
      filterAnimation: null,
      teamTagsData: null,
      totalVisibleItems: 0,
    };

    this.mount();
    this.init();
  }

  mount() {
    const { dom, state } = this;
    console.log('mounting TeamListing');

    dom.$filterModules.forEach(filter => {
      const filterObject = new TeamListingFilter(filter);
      const location = filter.dataset.location;
      state.filterInstances[location] = filterObject;

      filter.addEventListener(
        'filters:change',
        this.handleFilterEvent.bind(this)
      );

      filter.addEventListener(
        'filters:reset',
        this.handleFilterReset.bind(this)
      );
    });
  }

  init() {
    this.getTeamTagsData();
    this.syncFilterUI();
  }

  getTeamTagsData() {
    const { dom, state } = this;
    const teamTagsDataEl = dom.$teamTagsData;
    const teamTagsData = teamTagsDataEl ? JSON.parse(teamTagsDataEl.textContent) : {};
    state.teamTagsData = teamTagsData;
  }

  handleFilterEvent(e) {
    const { value, checked } = e.detail;
    const { state } = this;

    if (checked) {
      if (!state.activeFilters.includes(value)) {
        state.activeFilters.push(value);
      }
    } else {
      state.activeFilters = state.activeFilters.filter(v => v !== value);
    }

    this.animateFilterChange();
  }

  animateFilterChange() {
    const { dom, state } = this;

    // Kill any in-progress animation immediately
    if (state.filterAnimation) {
      state.filterAnimation.kill();
    }

    state.filterAnimation = gsap.timeline({ defaults: { ease: 'power1.out' } })
      .to(dom.$gridsWrap, {
        opacity: 0,
        duration: 0.2,
        pointerEvents: 'none',
        onComplete: () => {
          this.applyFilters();
          this.syncFilterUI();
          this.notifySidebar();
          this.updateEmptyState();
        }
      })
      .to(dom.$gridsWrap, {
        opacity: 1,
        duration: 0.25,
        pointerEvents: 'auto'
      });
  }

  applyFilters() {
    const { state, dom, refs } = this;

    let totalVisibleItems = 0;
    state.totalVisibleItems = 0;

    // If no filters are active, show everything
    if (state.activeFilters.length === 0) {
      dom.$grids.forEach($grid => {
        $grid.classList.remove('hide');
        $grid.querySelectorAll(refs.listItem).forEach($item => {
          totalVisibleItems++;
          $item.classList.remove('hide');
        });
      });
      state.totalVisibleItems = totalVisibleItems;
      return;
    }

    // ------------------------------------------
    // STEP 1: Build mapping of active filters to groups
    // ------------------------------------------
    // Use state.teamTagsData to know which filter belongs to which group
    const activeFiltersByGroup = {};

    state.activeFilters.forEach(filter => {
      let found = false;

      // Check grouped_tags
      state.teamTagsData.grouped_tags.forEach(groupData => {
        const groupSlug = groupData.group.slug; // assuming slug is unique key
        const groupTermSlugs = groupData.terms.map(t => t.slug);

        if (groupTermSlugs.includes(filter)) {
          if (!activeFiltersByGroup[groupSlug]) activeFiltersByGroup[groupSlug] = [];
          activeFiltersByGroup[groupSlug].push(filter);
          found = true;
        }
      });

      // Check other_tags
      if (!found) {
        const otherSlugs = state.teamTagsData.other_tags.map(t => t.slug);
        if (otherSlugs.includes(filter)) {
          if (!activeFiltersByGroup.other) activeFiltersByGroup.other = [];
          activeFiltersByGroup.other.push(filter);
        }
      }
    });

    // ------------------------------------------
    // STEP 2: Apply filtering (AND across groups, OR within group)
    // ------------------------------------------
    dom.$grids.forEach($grid => {
      const $listItems = $grid.querySelectorAll(refs.listItem);
      let gridHasVisible = false;

      $listItems.forEach($item => {
        let passes = true;

        for (const [group, filters] of Object.entries(activeFiltersByGroup)) {
          const itemTags = ($item.dataset[group] || '').split(' ');

          // If group has no active filters OR filter array is empty → no match exists → hide item
          if (!filters.length) {
            passes = false;
            break;
          }

          // OR within group
          const matchesGroup = filters.some(f => itemTags.includes(f));
          if (!matchesGroup) {
            passes = false;
            break;
          }
        }

        $item.classList.toggle('hide', !passes);
        if (passes) {
          gridHasVisible = true;
          totalVisibleItems++;
        }
      });

      $grid.classList.toggle('hide', !gridHasVisible);
      state.totalVisibleItems = totalVisibleItems;
    });
  }

  syncFilterUI() {
    const { state, dom } = this;

    const count = state.activeFilters.length;

    dom.$filterModules.forEach(filter => {
      const location = filter.dataset.location;

      state.filterInstances[location].updateState({
        count: count,
        activeFilters: state.activeFilters,
      });
    });
  }

  notifySidebar() {
    const { dom } = this;

    const event = new CustomEvent('teamlisting:filtersApplied', {
      detail: {
        visibleSectionIds: Array.from(dom.$grids)
          .filter($grid => !$grid.classList.contains('hide'))
          .map($grid => $grid.id)
      }
    });
    dom.$teamListingSidebar.dispatchEvent(event);
  }

  handleFilterReset(e) {
    e.preventDefault();

    const { state } = this;

    state.activeFilters = [];
    this.animateFilterChange();
  }

  updateEmptyState() {
    const { dom, state } = this;

    dom.$component.classList.toggle('no-filtered-results', state.totalVisibleItems === 0);
  }

}

const $teamListing = document.querySelector('.js-team-listing');
if ($teamListing) new TeamListing($teamListing);
